import { clsx } from "clsx";
import type { ReactNode } from "react";
import type { TextRole } from "@/types";
import "./Text.css";

// Covers the full heading range (h1–h6) so `Text` can absorb every element the
// retired Display/Heading rendered without downgrading heading semantics (ADR-0080).
type TextElement = "p" | "span" | "div" | "label" | "li" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

export interface TextProps {
  variant?: TextRole;
  as?: TextElement;
  muted?: boolean;
  className?: string;
  children: ReactNode;
}

const roleDefaultElement: Record<TextRole, TextElement> = {
  display: "h1",
  title: "h2",
  stat: "span",
  heading: "h3",
  subheading: "h4",
  "body-lg": "p",
  body: "p",
  "body-sm": "p",
  label: "span",
  caption: "span",
  eyebrow: "span",
};

export function Text({ variant = "body", as, muted = false, className, children }: TextProps) {
  const Element = as ?? roleDefaultElement[variant];

  return (
    <Element
      data-component="Text"
      data-variant={variant}
      className={clsx(`ds-text--${variant}`, { "ds-text--muted": muted }, className)}
    >
      {children}
    </Element>
  );
}

Text.displayName = "Text";
